import axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {IncomingMessage} from 'http';
import qs from 'qs';
import {HEADER_X_FORWARDED_FOR} from '../auth/constants';
import {getClientIp} from '../auth/getClientIp';
import {getUaFromServerSide} from '../bff/getUaFromServerSide';
import {getEnv} from '../common/env/env';
import {getTranslatedApiMessage} from '../common/getTranslatedApiMessage';
import {t} from '../common/getTranslations';
import {ApiStatusCodeVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {isTckApiUrl} from '../domain/ask/util/isTckApiUrl';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';

const DEFAULT_TIMEOUT_MS = 20000;

export interface FetcherOptions {
  ssrRequest?: IncomingMessage;
  cookie?: string;
  timeoutMs?: number;
  requestType?: 'ssr' | 'scheduler';
}

const axiosBffInstance = axios.create({
  transformRequest: [
    (data, headers) => {
      if (typeof data === 'string') {
        return data;
      }
      if (!data || !headers) {
        return data;
      }

      if (headers['Content-Type'] === 'application/json') {
        return JSON.stringify(data);
      }

      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }

      if (headers['Content-Type'] === 'application/x-www-form-urlencoded') {
        return qs.stringify(data);
      }

      return data;
    },
  ],
});

axiosBffInstance.interceptors.response.use(
  function (response) {
    if (
      !response.data ||
      typeof response.data !== 'object' ||
      Array.isArray(response.data)
    ) {
      return response;
    }
    const url = response.config.url;
    const isGatewayPath = url?.split('?')[0]?.includes('/api/gateway/');

    if (!isGatewayPath) {
      return {
        ...response,
        data: {
          ...response.data,
          message: getTranslatedApiMessage(response.data),
          original_message:
            response.data.original_message ?? response.data.message,
        },
      };
    }

    // Gateway 경로 중 bypass가 필요한 케이스
    const shouldBypass =
      // 댓글작성 차단시 실패사유 정보를 받아서 바운더리가 아닌 화면단에서 처리해야 하므로 bypass합니다.
      url?.includes('/api/v5/store/community/write/comment') &&
      response.data.result_code === ApiStatusCodeVo.CommentWriteFailBlocked;

    if (response.data.result_code < 0) {
      if (!shouldBypass) {
        throw new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.NicolasApiStatusError,
          resultCode: response.data.result_code,
          message: getTranslatedApiMessage(response.data),
          originalMessage: response.data.message,
        });
      }
    }
    return {
      ...response,
      data: {
        ...response.data,
        message: getTranslatedApiMessage(response.data),
        original_message:
          response.data.original_message ?? response.data.message,
      },
    };
  },
  function (error: AxiosError) {
    if (!error.response) {
      const _error = new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.NetworkStatusError,
        message: t('error_network_title'),
      });
      return Promise.reject(_error);
    }

    if (error.response?.config.url && isTckApiUrl(error.response.config.url)) {
      const errorData = error.response.data as {
        message?: string;
        result_code?: number;
      };
      return Promise.reject(
        new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.TckApiStatusError,
          message: errorData.message,
          resultCode: errorData.result_code,
        })
      );
    }

    return Promise.reject(error);
  }
);

export function axiosBffFetch(
  url: string,
  config?: AxiosRequestConfig,
  options?: FetcherOptions
) {
  // 만약 path 만 들어온 경우 앞에 bffOrigin 을 붙여준다
  const bffUrl = url.startsWith('/') ? `${getEnv().bffOrigin}${url}` : url;

  const axiosConfig: AxiosRequestConfig = {
    url: bffUrl,
    withCredentials: IS_CLIENT_SIDE,
    timeout: options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    ...config,
  };

  // SSR 환경일 때 헤더 설정
  if (!IS_CLIENT_SIDE && options?.ssrRequest) {
    axiosConfig.headers = {
      ...axiosConfig.headers,
      ...(options.ssrRequest.headers as Record<string, string>),
      ...(options.cookie && {cookie: options.cookie}),
      'user-agent': getUaFromServerSide({
        request: options.ssrRequest,
        requestType: options.requestType === 'scheduler' ? 'scheduler' : 'ssr',
      }),
      host: new URL(getEnv().bffOrigin).host,
      [HEADER_X_FORWARDED_FOR]: getClientIp(options.ssrRequest),
    };
  }

  return axiosBffInstance(axiosConfig);
}
